import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  acceptOfferLeaseAgreement,
  createLeaseAgreement,
  deleteLeaseAgreement,
  generateLeaseAgreement,
  getLeaseAgreement,
  updateLeaseAgreement,
  bulkCreateLeaseAgreement,
} from "libs/api/lease_agreement";

// LeaseAgreement Thunk
export const getLeaseAgreementThunk = createAsyncThunk(
  "lease-agreement/get",
  async ({
    cbPending,
    cbFulfilled,
    cbRejected,
    cbFinally,
    pathname,
    queryParams,
    headers,
  }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await getLeaseAgreement(pathname, queryParams, headers);
      typeof cbFulfilled === "function" && cbFulfilled();
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

// Create LeaseAgreement Thunk
export const createLeaseAgreementThunk = createAsyncThunk(
  "lease-agreement/create",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, pathname, body }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await createLeaseAgreement(pathname, body);
      typeof cbFulfilled === "function" && cbFulfilled();
      return response;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

// Update LeaseAgreement Thunk
export const updateLeaseAgreementThunk = createAsyncThunk(
  "lease-agreement/update",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, pathname, body }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await updateLeaseAgreement(pathname, body);
      typeof cbFulfilled === "function" && cbFulfilled();
      return response;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

export const acceptOfferLeaseAgreementThunk = createAsyncThunk(
  "lease-agreement/accept",
  async ({
    cbPending,
    cbFulfilled,
    cbRejected,
    cbFinally,
    pathname,
    body,
    accessToken,
  }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await acceptOfferLeaseAgreement(
        pathname,
        body,
        accessToken,
      );
      typeof cbFulfilled === "function" && cbFulfilled();
      return response;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

// Delete LeaseAgreement Thunk
export const deleteLeaseAgreementThunk = createAsyncThunk(
  "lease-agreement/delete",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, pathname, body }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await deleteLeaseAgreement(pathname, body);
      typeof cbFulfilled === "function" && cbFulfilled();
      return response;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

export const generateLeaseAgreementThunk = createAsyncThunk(
  "lease-agreement/generate",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, pathname }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await generateLeaseAgreement(pathname);
      typeof cbFulfilled === "function" && cbFulfilled();
      return response;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

export const getLeaseAgreementDetailThunk = createAsyncThunk(
  "lease-agreement/detail",
  async ({
    cbPending,
    cbFulfilled,
    cbRejected,
    cbFinally,
    id, // <— WAJIB: id lease agreement
    pathname, // <— opsional; default ke `/lease-agreement/${id}`
  }) => {
    try {
      typeof cbPending === "function" && cbPending();

      // Jika caller tidak memberi pathname, fallback ke /lease-agreement/:id
      const detailPath = pathname ?? `/lease-agreement/${id}`;

      // Asumsi getLeaseAgreement(path, query) bisa memanggil path detail langsung
      const response = await getLeaseAgreement(
        detailPath,
        /* queryParams= */ "",
      );
      typeof cbFulfilled === "function" && cbFulfilled();
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

// Bulk Create LeaseAgreement Thunk
export const bulkCreateLeaseAgreementThunk = createAsyncThunk(
  "lease-agreement/bulkCreate",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, offerings }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await bulkCreateLeaseAgreement(offerings);
      typeof cbFulfilled === "function" && cbFulfilled(response?.data);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);
